import { useEffect, useState, useCallback, useRef, useMemo, Fragment } from "react";
import { Search, ImageOff, ChevronDown, ShoppingCart } from "lucide-react";
import { fmtDate, statusLabel, statusClass, unitLabel } from "../lib/format";
import api from "../lib/api";
import { fmtMoney } from "../lib/format";
import { Input } from "../components/ui/input";
import { SearchInput } from "../components/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { VariantBadge } from "../components/VariantBadge";

const ALL = "__all__";

export const mediaUrl = (path) =>
  path ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${path.replace(/^\/api\/v1\/media\//, "")}` : null;

export const ProductThumb = ({ src, alt, size = "h-10 w-10" }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed)
    return (
      <div className={`flex ${size} items-center justify-center border border-stone-200 bg-stone-50 text-stone-300`}>
        <ImageOff size={14} />
      </div>
    );
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${size} border border-stone-200 object-cover`}
    />
  );
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(ALL);
  const [selected, setSelected] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [mediaOk, setMediaOk] = useState(false);
  const [orderStats, setOrderStats] = useState(null);
  const mediaChecked = useRef(false);
  const debounce = useRef(null);

  useEffect(() => {
    api
      .get("/orders/by-product")
      .then((r) => {
        const map = {};
        r.data.data.forEach((g) => g.products.forEach((p) => { if (p.sku) map[p.sku] = p; }));
        setOrderStats(map);
      })
      .catch(() => setOrderStats({}));
  }, []);

  useEffect(() => {
    if (mediaChecked.current) return;
    const first = products.find((p) => p.images?.[0]);
    if (!first) return;
    mediaChecked.current = true;
    fetch(mediaUrl(first.images[0]), { credentials: "include" })
      .then((r) => setMediaOk(r.ok))
      .catch(() => setMediaOk(false));
  }, [products]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/products", { params: { search: search || undefined, category: category !== ALL ? category : undefined } })
      .then((r) => {
        setProducts(r.data.data);
        setTotal(r.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, category]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(debounce.current);
  }, [load, search]);

  const catTr = useMemo(() => Object.fromEntries(categories.map((c) => [c.name, c.nameTr || c.name])), [categories]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const key = p.category || "Diğer";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], "tr"));
  }, [products]);

  return (
    <div className="space-y-4" data-testid="products-page">
      <div className="flex flex-wrap items-center gap-3 border border-stone-200 bg-white p-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Ürün adı veya SKU ara..." testId="products-search-input" className="min-w-[220px] flex-1" />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[240px] rounded-sm border-stone-300" data-testid="filter-category-select">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={ALL}>Tüm Kategoriler</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.name}>
                {c.nameTr || c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-stone-200 bg-white">
        <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Katalog</p>
          <p className="text-xs text-stone-500" data-testid="products-count">
            {total} ürün
          </p>
        </div>
        {loading ? (
          <div className="flex h-40 items-center justify-center" data-testid="products-loading">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#1C4E35] border-t-transparent" />
          </div>
        ) : products.length === 0 ? (
          <p className="p-8 text-center text-sm text-stone-500" data-testid="products-empty">
            Ürün bulunamadı.
          </p>
        ) : (
          <Table data-testid="products-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-14 text-xs">Görsel</TableHead>
                <TableHead className="text-xs">Ürün</TableHead>
                <TableHead className="text-xs">Alt Kategori</TableHead>
                <TableHead className="text-xs">Birim</TableHead>
                <TableHead className="text-right text-xs">Erken</TableHead>
                <TableHead className="text-right text-xs">Standart</TableHead>
                <TableHead className="text-right text-xs">Geç</TableHead>
                <TableHead className="text-right text-xs">Min. Adet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map(([cat, items]) => (
                <Fragment key={cat}>
                  <TableRow
                    onClick={() => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }))}
                    className="cursor-pointer select-none hover:bg-stone-200/60"
                    data-testid={`category-group-${cat}`}
                  >
                    <TableCell colSpan={8} className="border-y border-stone-200 bg-stone-100 py-2">
                      <span className="flex items-center gap-2">
                        <ChevronDown
                          size={14}
                          className={`text-stone-500 transition-transform duration-200 ${collapsed[cat] ? "-rotate-90" : ""}`}
                        />
                        <span className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-[#1C4E35]">
                          {catTr[cat] || cat}
                        </span>
                        <span className="text-[11px] text-stone-500">{items.length} ürün</span>
                      </span>
                    </TableCell>
                  </TableRow>
                  {!collapsed[cat] &&
                    items.map((p) => (
                    <TableRow key={p.sku} onClick={() => setSelected(p)} className="cursor-pointer transition-colors duration-150 hover:bg-stone-50" data-testid={`product-row-${p.sku}`}>
                      <TableCell className="py-2">
                        <ProductThumb src={mediaOk ? mediaUrl(p.images?.[0]) : null} alt={p.name} />
                      </TableCell>
                      <TableCell className="py-2">
                        <p className="text-sm font-medium">{p.nameTr || p.name}</p>
                        <p className="text-[11px] text-stone-400">{p.sku}</p>
                      </TableCell>
                      <TableCell className="py-2 text-xs text-stone-500">{p.subCategory || "—"}</TableCell>
                      <TableCell className="py-2 text-xs capitalize text-stone-500">{p.unit}</TableCell>
                      <TableCell className="py-2 text-right text-sm">{p.price?.early != null ? fmtMoney(p.price.early, p.currency) : "-"}</TableCell>
                      <TableCell className="py-2 text-right text-sm">{p.price?.standard != null ? fmtMoney(p.price.standard, p.currency) : "-"}</TableCell>
                      <TableCell className="py-2 text-right text-sm">{p.price?.late != null ? fmtMoney(p.price.late, p.currency) : "-"}</TableCell>
                      <TableCell className="py-2 text-right text-sm">{p.minOrderQty ?? "-"}</TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent aria-describedby={undefined} className="max-h-[85vh] max-w-lg overflow-y-auto rounded-sm bg-white" data-testid="product-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg font-bold tracking-tight">
                  {selected.nameTr || selected.name}
                </DialogTitle>
              </DialogHeader>
              <ProductThumb src={mediaOk ? mediaUrl(selected.images?.[0]) : null} alt={selected.name} size="h-56 w-full" />
              <p className="text-[11px] uppercase tracking-wider text-stone-400">{selected.sku} · {selected.category}</p>
              {(selected.descriptionTr || selected.description) && (
                <p className="text-sm text-stone-600">{selected.descriptionTr || selected.description}</p>
              )}
              <div className="grid grid-cols-3 gap-px border border-stone-200 bg-stone-200">
                {[["Erken", selected.price?.early], ["Standart", selected.price?.standard], ["Geç", selected.price?.late]].map(([label, val]) => (
                  <div key={label} className="bg-white p-3">
                    <p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p>
                    <p className="font-heading mt-1 text-lg font-bold">{val != null ? fmtMoney(val, selected.currency) : "-"}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-6 text-xs text-stone-500">
                <span>Birim: <span className="capitalize text-stone-700">{selected.unit}</span></span>
                <span>Min. Adet: <span className="text-stone-700">{selected.minOrderQty ?? "-"}</span></span>
                <span>Bölge: <span className="capitalize text-stone-700">{selected.zone}</span></span>
              </div>
              <div className="border-t border-stone-200 pt-3" data-testid="product-order-summary">
                <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-stone-500">
                  <ShoppingCart size={13} className="text-[#1C4E35]" /> Sipariş Özeti
                </p>
                {(() => {
                  const st = orderStats?.[selected.sku];
                  if (!orderStats)
                    return <p className="mt-2 text-xs text-stone-400">Yükleniyor...</p>;
                  if (!st)
                    return <p className="mt-2 text-sm text-stone-500">Bu ürün için henüz sipariş verilmemiş.</p>;
                  return (
                    <>
                      <div className="mt-2 grid grid-cols-3 gap-px border border-stone-200 bg-stone-200">
                        {[["Toplam Miktar", `${st.qty} ${unitLabel(st.unit)}`], ["Sipariş Sayısı", st.orderCount], ["Toplam Tutar", fmtMoney(st.total, st.currency)]].map(([label, val]) => (
                          <div key={label} className="bg-white p-3">
                            <p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p>
                            <p className="font-heading mt-1 text-lg font-bold text-[#1C4E35]">{val}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 max-h-40 overflow-y-auto">
                        {st.orderers.map((o, i) => (
                          <div key={`${o.ref}-${i}`} className="flex items-center justify-between border-b border-stone-100 py-1.5 text-sm">
                            <div className="min-w-0">
                              <p className="truncate font-medium">{o.organisation}</p>
                              <p className="text-[11px] text-stone-400">{o.ref} · {fmtDate(o.placedAt)}</p>
                            </div>
                            <div className="ml-3 flex shrink-0 items-center gap-2">
                              {o.variant && <VariantBadge variant={o.variant} />}
                              <span className={`border px-1.5 py-0.5 text-[10px] font-medium ${statusClass(o.status)}`}>{statusLabel(o.status)}</span>
                              <span className="font-heading text-sm font-bold">{o.qty} {unitLabel(st.unit)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
